import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './LanguageSettings.css';
import { AppHeader, BottomNavigation, CreateOptionsMenu, BackButton } from './shared';
import { useLogout, useUserData } from '../hooks';
import { useNavigation } from '../contexts/NavigationContext';
import { useLanguage } from '../contexts/LanguageContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

function LanguageSettings({ onLanguageChange }) {
  const navigation = useNavigation();
  const { userData } = useUserData();
  const { handleLogout, isLoggingOut, logoutError, clearLogoutError } = useLogout(navigation.onLogout);
  const { language: globalLanguage, setLanguage: setGlobalLanguage, t } = useLanguage();

  const [selectedLanguage, setSelectedLanguage] = useState(globalLanguage || 'Deutsch');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showCreateOptions, setShowCreateOptions] = useState(false);

  // Set initial language from user preferences when userData loads
  useEffect(() => {
    if (userData?.preferred_language) {
      setSelectedLanguage(userData.preferred_language);
      setGlobalLanguage(userData.preferred_language);
    }
  }, [userData, setGlobalLanguage]);

  const handleLanguageChange = async (language) => {
    if (language === selectedLanguage) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      const response = await axios.put(`${API_BASE_URL}/api/user/preferences`, {
        preferred_language: language
      });

      if (response.data.success) {
        setSelectedLanguage(language);
        setGlobalLanguage(language); // Update global language state
        // Notify parent component about language change
        if (onLanguageChange) {
          onLanguageChange(language);
        }
      }
    } catch (err) {
      console.error('Failed to update language preference:', err);
      setSaveError(t('languageSettings.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="language-settings-page">
      {/* Header Navigation Bar - Using shared AppHeader component */}
      <AppHeader
        userData={userData}
        onLogout={handleLogout}
        isLoggingOut={isLoggingOut}
        logoutError={logoutError}
        onClearLogoutError={clearLogoutError}
      />

      {/* Tab Navigation */}
      <div className="tab-navigation-language">
        <span className="tab-title">{t('languageSettings.title')}</span>
      </div>

      {/* Language Settings Content */}
      <main className="language-settings-content">
        {/* Back Button */}
        <BackButton
          onClick={navigation.onNavigateBack}
          className="back-button-content"
        />

        {/* Title */}
        <div className="language-title">
          <span>{t('languageSettings.setPreferred')}</span>
        </div>

        {/* Save Error */}
        {saveError && (
          <div className="save-error-message">
            {saveError}
          </div>
        )}

        {/* Language Options */}
        <div className="language-options">
          <label className="language-option">
            <input
              type="radio"
              name="language"
              value="Deutsch"
              checked={selectedLanguage === 'Deutsch'}
              onChange={() => handleLanguageChange('Deutsch')}
              disabled={isSaving}
            />
            <span className="radio-custom"></span>
            <span className="language-label">{t('languageSettings.german')}</span>
          </label>

          <label className="language-option">
            <input
              type="radio"
              name="language"
              value="Englisch"
              checked={selectedLanguage === 'Englisch'}
              onChange={() => handleLanguageChange('Englisch')}
              disabled={isSaving}
            />
            <span className="radio-custom"></span>
            <span className="language-label">{t('languageSettings.english')}</span>
          </label>
        </div>

        {isSaving && (
          <div className="saving-indicator">
            {t('languageSettings.saving')}
          </div>
        )}
      </main>

      {/* Bottom Navigation Bar - Using shared component */}
      <BottomNavigation
        activeTab="profile"
        onCreateClick={() => setShowCreateOptions(!showCreateOptions)}
      />

      {/* Create Options Menu - Using shared component */}
      <CreateOptionsMenu
        isOpen={showCreateOptions}
        onClose={() => setShowCreateOptions(false)}
      />
    </div>
  );
}

export default LanguageSettings;
